import { useEffect, useRef, useState } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';

import axiosInstance from '../../services/axiosInstance';
import { eventService } from '../../services/eventService';

interface DocProcessingState {
  filename: string;
  status: string;
  progress: number;
  batch: string;
  projectId: string;
  documentId?: string;
  canRetry?: boolean;
  errorMessage?: string;
  creditsDeducted?: number;
  pageCount?: number;
  batchComplete?: boolean;
  completedBatches?: number;
  totalBatches?: number;
}

interface DocProcessingEvent {
  status?: string;
  filename?: string;
  progress?: number;
  batch?: string;
  project_id?: string;
  document_id?: string;
  can_retry?: boolean;
  error_message?: string;
  credits_deducted?: number;
  page_count?: number;
  batch_complete?: boolean;
  completed_batches?: number;
  total_batches?: number;
}

type DismissTimerRef = MutableRefObject<ReturnType<typeof setTimeout> | null>;
type DocProcessingSetter = Dispatch<SetStateAction<DocProcessingState | null>>;

const isTerminalEvent = (event: DocProcessingEvent): boolean => (
  event.status === 'completed' || event.status === 'complete'
);

const cancelDismiss = (timerRef: DismissTimerRef): void => {
  if (!timerRef.current) return;
  clearTimeout(timerRef.current);
  timerRef.current = null;
};

const terminalState = (
  previous: DocProcessingState | null,
  event: DocProcessingEvent,
): DocProcessingState | null => previous && ({
  ...previous,
  progress: 100,
  status: event.status ?? 'complete',
  creditsDeducted: event.credits_deducted,
  pageCount: event.page_count,
  batchComplete: event.batch_complete,
  completedBatches: event.completed_batches,
  totalBatches: event.total_batches,
});

const activeState = (event: DocProcessingEvent): DocProcessingState => ({
  filename: event.filename ?? '',
  status: event.status ?? 'processing',
  progress: event.progress ?? 0,
  batch: event.batch ?? '',
  projectId: event.project_id ?? '',
  documentId: event.document_id,
  canRetry: event.can_retry,
  errorMessage: event.error_message,
  batchComplete: event.batch_complete,
  completedBatches: event.completed_batches,
  totalBatches: event.total_batches,
});

const applyTerminalEvent = (
  event: DocProcessingEvent,
  setState: DocProcessingSetter,
  timerRef: DismissTimerRef,
): void => {
  setState((previous) => terminalState(previous, event));
  if (event.batch_complete === false) return;
  timerRef.current = setTimeout(() => {
    setState(null);
    timerRef.current = null;
  }, 3500);
};

const applyDocProcessingEvent = (
  data: unknown,
  setState: DocProcessingSetter,
  timerRef: DismissTimerRef,
  fromSnapshot = false,
): void => {
  const event = data as DocProcessingEvent;

  cancelDismiss(timerRef);
  if (isTerminalEvent(event)) {
    applyTerminalEvent(event, setState, timerRef);

    return;
  }
  setState((previous) => {
    if (event.status === 'excluded') return previous?.documentId === event.document_id ? null : previous;

    return !fromSnapshot && previous && ['error', 'waiting_credits'].includes(previous.status) && previous.documentId
      && previous.documentId !== event.document_id && !['error', 'waiting_credits'].includes(event.status ?? '')
      ? previous : activeState(event);
  });
};

/**
 * Subscribes to `doc_processing` SSE events and exposes the current
 * document processing state.  Auto-clears after the progress reaches
 * completion. Failures stay visible until processing is explicitly retried.
 *
 * Agent suggestion buttons are handled by the worker, which runs a
 * supervisor check after processing and broadcasts `suggested_agents`
 * via SSE.  The eventService bridges that to window.CustomEvent.
 */
export const useDocProcessing = (projectId?: string | null): DocProcessingState | null => {
  const [state, setState] = useState<DocProcessingState | null>(null);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!projectId) return undefined;

    return subscribeDocumentProgress(projectId, setState, dismissTimerRef);
  }, [projectId]);

  return state?.projectId === projectId ? state : null;
};

// eslint-disable-next-line max-lines-per-function, max-statements
function subscribeDocumentProgress(
  projectId: string, setState: DocProcessingSetter, timerRef: DismissTimerRef,
): () => void {
  let active = true;
  let revision = 0;
  let restoring = false;
  const accept = (data: unknown, fromSnapshot = false) => {
    if (!active || (data as DocProcessingEvent)?.project_id !== projectId) return;
    revision += 1;
    applyDocProcessingEvent(data, setState, timerRef, fromSnapshot);
  };
  // eslint-disable-next-line max-statements
  const restore = async () => {
    if (restoring) return;
    restoring = true;
    const beforeRequest = revision;

    try {
      const { data } = await axiosInstance.get<{ processing: DocProcessingEvent | null }>(
        `/api/v1/projects/${projectId}/doc-processing`,
      );

      if (!active || beforeRequest !== revision) return;
      if (data.processing) accept(data.processing, true);
      else setState((previous) => previous && isTerminalEvent(previous) ? previous : null);
    } catch {
      // Preserve the last known progress during connection interruptions.
    } finally {
      restoring = false;
    }
  };
  const unsubscribe = eventService.subscribe('doc_processing', accept);
  const reconnect = eventService.subscribe('connected', () => { void restore(); });
  const interval = setInterval(() => { void restore(); }, 20000);

  void restore();

  return () => { active = false; clearInterval(interval); cancelDismiss(timerRef); unsubscribe(); reconnect(); };
}


// ============================================================================
// useDocUploadChoice — shows the Replace/Merge/Supplement prompt when a
// second document is uploaded to a project that already has an SRD.
// ============================================================================

export interface DocUploadChoiceState {
  filename: string;
  jobKey: string;
  projectId: string;
  recommended: 'replace' | 'merge';
}

type ChoiceSetter = Dispatch<SetStateAction<DocUploadChoiceState[]>>;

function subscribeDocumentChoices(projectId: string, dismissed: Set<string>, setChoices: ChoiceSetter): () => void {
  let active = true;
  const accept = (data: unknown) => {
    const d = data as { filename?: string; job_key?: string; project_id?: string; recommended?: string };

    if (!active || d.project_id !== projectId || !d.filename || !d.job_key) return;
    if (dismissed.has(d.job_key)) return;
    const next: DocUploadChoiceState = {
      filename: d.filename, jobKey: d.job_key, projectId,
      recommended: d.recommended === 'replace' ? 'replace' : 'merge',
    };

    setChoices((items) => items.some((item) => item.jobKey === next.jobKey) ? items : [...items, next]);
  };
  const restore = async () => {
    try {
      const { data } = await axiosInstance.get<{ choices: unknown[] }>(`/api/v1/projects/${projectId}/doc-choices`);

      if (active) data.choices.forEach(accept);
    } catch {
      // Reconnecting retries the durable inventory; existing choices remain visible.
    }
  };
  const unsubscribe = eventService.subscribe('doc_upload_choice', accept);
  const reconnect = eventService.subscribe('connected', () => { void restore(); });

  void restore();

  return () => { active = false; unsubscribe(); reconnect(); };
}

/**
 * Subscribes to `doc_upload_choice` SSE events.
 * Returns the pending choice state (cleared once the user picks an option).
 */
export const useDocUploadChoice = (projectId?: string | null): [
  DocUploadChoiceState | null,
  () => void,
] => {
  const dismissedRef = useRef(new Set<string>());
  const [choices, setChoices] = useState<DocUploadChoiceState[]>([]);
  const choice = choices.find((item) => item.projectId === projectId) ?? null;
  const dismiss = () => {
    if (choice) dismissedRef.current.add(choice.jobKey);
    setChoices((items) => items.filter((item) => item.jobKey !== choice?.jobKey));
  };

  useEffect(() => {
    if (!projectId) return undefined;

    return subscribeDocumentChoices(projectId, dismissedRef.current, setChoices);
  }, [projectId]);

  return [choice, dismiss];
};
